import { useState, useEffect } from "react";
import { notificationsService, SellerNotification } from "@/services/notificationsService";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { badgeService } from "@/services/badgeService";

export const useSellerNotifications = () => {
  const [notifications, setNotifications] = useState<SellerNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const [notificationsData, unreadCountData] = await Promise.all([
        notificationsService.getSellerNotifications(user.id),
        notificationsService.getUnreadCount(user.id)
      ]);
      
      setNotifications(notificationsData);
      setUnreadCount(unreadCountData);
      
      // Sync app icon badge with unread count
      badgeService.setBadgeCount(unreadCountData);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast({
        title: "Error",
        description: "Failed to load notifications",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      await notificationsService.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read_status: true }
            : notification
        )
      );
      const newCount = Math.max(0, unreadCount - 1);
      setUnreadCount(newCount);
      
      // Update app icon badge
      badgeService.setBadgeCount(newCount);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!user?.id) return;
    
    try {
      await notificationsService.markAllAsRead(user.id);
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read_status: true }))
      );
      setUnreadCount(0);
      
      // Clear app icon badge
      badgeService.clearBadge();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Set up real-time subscription with Safari error protection
  useEffect(() => {
    if (!user?.id) return;

    fetchNotifications();

    let channel: ReturnType<typeof notificationsService.subscribeToNotifications> | null = null;

    try {
      channel = notificationsService.subscribeToNotifications(
        user.id,
        (payload) => {
          console.log('New notification received:', payload);
          fetchNotifications(); // Refresh notifications when new one arrives
          
          const notificationType = payload.new?.type;
          const notificationMessages = {
            property_offer: { title: "💰 New Offer Received", description: "A buyer has submitted an offer on your property" },
            offer_withdrawn: { title: "🔔 Offer Withdrawn", description: "A buyer withdrew their offer on your property" },
            property_view: { title: "👁️ New Property View", description: "A buyer has viewed one of your properties" },
            task_reminder: { title: "📋 Task Reminder", description: "You have a task that needs attention" }
          };
          
          const message = notificationMessages[notificationType as keyof typeof notificationMessages] || notificationMessages.property_view;
          
          toast({
            title: message.title,
            description: message.description,
          });
        }
      );
    } catch (error) {
      // Gracefully handle Safari/iOS SecurityError for WebSocket connections
      console.warn('Failed to subscribe to seller notifications (Safari security restriction?):', error);
      // App continues to work - notifications load on page refresh
    }

    return () => {
      try {
        if (channel) {
          channel.unsubscribe();
        }
      } catch (error) {
        console.warn('Error unsubscribing from seller notifications channel:', error);
      }
    };
  }, [user?.id]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    fetchNotifications
  };
};
